import React, { useCallback, useRef, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  checkPlaceholders,
  highlightPlaceholder,
  removeClassesFromHtml,
  sanitizeHtml,
  catchError,
} from "@_utils/helpers";
import { caseActions } from "@_actions/case.actions";
import DialogPopup from "@_components/DialogPopup/DialogPopup";
import ErrorMessagePopup from "../ErrorMessagePopup/ErrorMessagePopup";
import ErrorIcon from "@mui/icons-material/Error";
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import styles from "./MailPopup.module.css";
import AreYouSurePopup from "@_components/popups/AreYouSurePopup/AreYouSurePopup";

import RichTextEditor from "@_components/RichTextEditor/RichTextEditor";
import { caseConstants } from "@_constants/case.constants";
import { emailConstants } from "@_constants/email_flows.constants";
import AddressComponent from "./components/AddressComponent/AddressComponent";
import Scrollbar from "react-perfect-scrollbar-z";
import { invoiceTemplateService } from "@_services/invoiceTemplate.service";
import AppSnackbar from "@_components/AppSnackbar/AppSnackbar";
import { useSnackbar } from "@_hooks/useSnackbar";
import { useEditorInitialization } from "./hooks/useEditorInitialization";
import { useReminderState } from "./hooks/useReminderState";
import { useEmailSendStatus } from "./hooks/useEmailSendStatus";
import AttachmentsRow from "./components/AttachmentsRow";
import ReminderControls from "./components/ReminderControls";
import InvoicePicker from "./components/InvoicePicker";
import LetterCostDialog from "./components/LetterCostDialog";
import PopupFooter from "./components/PopupFooter";

const defaultState = {
  to: "",
  subject: "",
  content: "",
  // bcc: '',
};

/*
  --- HIGH PRIORITY ISSUES TO FIX ---

  1. Overlapping useEffect hooks: RESOLVED.
      Replaced by `useEditorInitialization` (./hooks/useEditorInitialization.js),
      which centralizes reply / non-reply editor + input initialization behind a
      single editorReady-gated effect.

  2. setTimeout usage is incorrect:
      setTimeout(() => dispatch(casesActions.getCases(), 500));
      // The 500 is passed to dispatch, not setTimeout. Should be:
      // setTimeout(() => dispatch(casesActions.getCases()), 500);

  3. Accessing caseReducer.case[caseId] without null checks:
      If caseReducer.case or caseReducer.case[caseId] is missing (slow load, wrong id, race), this throws 
      and the whole popup crashes.
      Add checks: const caseData = caseReducer.case ? caseReducer.case[caseId] : null;

  4. Typo in error message:
      "Failed to download Invoice for attachment." should be "download".

  5. Inefficient array filtering in addChoosenInvoicesToMail:
      Consider building a map for faster lookups.

  6. Large component:
      Consider splitting into smaller components for maintainability.
      
  --- END HIGH PRIORITY ISSUES ---
*/

function MailPopup(props) {
  const {
    data,
    caseId,
    emailSetup,
    activity,
    resumeAutomation,
    email,
    replyToAll,
    JwtData,
    isReplyMode,
    title,
    invoice,
  } = props;
  const editorInstance = useRef(null);
  const [editorReady, setEditorReady] = useState(false);
  const isClosingRef = useRef(false);
  const closePopup = props.close;
  const [attachments, setAttachments] = useState([]);

  const caseReducer = useSelector((state) => state.caseReducer);
  const customerReducer = useSelector((state) => state.customerReducer);
  // Defensive access: caseReducer.case[caseId] can be missing during slow load,
  // wrong id, or race conditions; without these guards the popup crashes.
  const caseData = caseReducer?.case?.[caseId];
  const opponentContactDetails = caseData?.opponent_contact_details ?? null;
  const userReducer = useSelector((state) => state.userReducer);
  const caseActivityReducer = useSelector((state) => {
    return state.caseActivityReducer;
  });
  const autoHideDuration = 3000;
  const {
    snackbarProps,
    showSnackbarSuccess,
    showSnackbarError,
    resetSnackbar,
  } = useSnackbar({
    autoHideDuration,
  });
  const contentRef = useRef(null);
  const dispatch = useDispatch();
  // Check for the missing placeholder on the first data loading
  const [missingPlaceholder, setMissingPlaceholder] = React.useState(false);
  // Update the content of text Editor change
  const [editedText, setEditedText] = React.useState();
  // Display send component or copy to clip base on the user server setup
  const [sendEmailTag, setSendEmailTag] = React.useState(true);
  const [inputs, setInputs] = useState(defaultState);

  // Snackbar and error message popup
  const [errorSnackbarTitle, setErrorSnackbarTitle] = useState("");
  const [errorSnackbar, setErrorSnackbar] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadedTemplate, setLoadedTemplate] = useState(null);

  const shouldFetchTemplate =
    props.open &&
    !isReplyMode &&
    data?.name !== emailConstants.NEW_MAIL &&
    !!data?.template_id &&
    data?.content == null &&
    data?.subject == null;

  const popupData =
    data?.content != null || data?.subject != null || !loadedTemplate
      ? data
      : {
          ...data,
          content: loadedTemplate.content,
          subject: loadedTemplate.subject,
        };

  // Reminder state + handlers (menu, date picker, name, saveDeadline) live
  // in a dedicated hook so MailPopup only wires them into the UI.
  const {
    reminderMenuOpen,
    reminderSetting,
    showDatePicker,
    reminderDate,
    setReminderDate,
    doSaveDeadline,
    reminderName,
    setReminderName,
    showAreYouSurePopup,
    reminderMinDate,
    handleOpenReminderMenu,
    handleCloseReminderMenu,
    handleChangeReminderOption,
    setReminderChecked,
    handleDeleteReminder,
    handleAreYouSurePopupClose,
    saveDeadline,
  } = useReminderState({
    caseId,
    caseData,
    dataName: data.name,
    isReplyMode,
  });

  // invoice attachments
  const [chooseInvoicePopupOpen, setChooseInvoicePopupOpen] = useState(false);
  const [noInvoicesPopupOpen, setNoInvoicesPopupOpen] = useState(false);
  const [choosenInvoices, setChoosenInvoices] = useState([]);
  const [didPreviewLetter, setDidPreviewLetter] = useState(false);
  const [hasUnreviewedChanges, setHasUnreviewedChanges] = useState(false);
  const [openLetterCostPopup, setOpenLetterCostPopup] = useState(false);

  useEffect(() => {
    if (invoice && props.open && attachments.length === 0) {
      downloadAndAddInvoice(invoice);
    }
    // eslint-disable-next-line
  }, [invoice, props.open]);

  const setTextToContent = (content, goToFirstLine) => {
    // Don't update editor if popup is closing
    if (isClosingRef.current || !content) return;
    const cleanContent = sanitizeHtml(content);
    cleanContent && setMissingPlaceholder(checkPlaceholders(cleanContent));
    const newContent = cleanContent && highlightPlaceholder(cleanContent);
    if (editorInstance.current && editorInstance.current.setContents) {
      // In standby mode the editor is read-only; temporarily unlock to allow
      // programmatic content insertion, then restore read-only state.
      if (editorInstance.current.readOnly) {
        editorInstance.current.readOnly(false);
      }
      editorInstance.current.setContents("");
      editorInstance.current.insertHtmlNoNewLine(newContent, goToFirstLine);
      if (
        editorInstance.current.readOnly &&
        caseData?.state === caseConstants.CASE_STATE_STANDBY
      ) {
        editorInstance.current.readOnly(true);
      }
    }
    setEditedText(newContent);
  };

  /**
   * Single source of truth for editor / input initialization.
   * Replaces three overlapping useEffects (reply init, non-reply with
   * signature, non-reply duplicate) that previously raced on
   * `data.content` and could overwrite each other.
   */
  useEditorInitialization({
    open: props.open,
    editorReady,
    isClosingRef,
    isReplyMode,
    loading,
    data: popupData,
    email,
    isSelfReply: props.isSelfReply,
    replyToAll,
    JwtData,
    emailSetup,
    userSignature: userReducer.user_signature,
    setTextToContent,
    setInputs,
    setSendEmailTag,
    setErrorSnackbar,
    setDidPreviewLetter,
    setMissingPlaceholder,
  });

  useEffect(() => {
    if (!shouldFetchTemplate) {
      setLoadedTemplate(null);
      return;
    }

    setLoadedTemplate(null);
    setLoading(true);
    dispatch(caseActions.getCaseTemplate(caseId, data.template_id));
  }, [dispatch, shouldFetchTemplate, caseId, data.template_id]);

  useEffect(() => {
    if (!props.open || !shouldFetchTemplate) {
      setLoading(false);
      return;
    }

    if (
      caseReducer.status.CASE_GET_TEMPLATE === "success" &&
      caseReducer.selected_case_template
    ) {
      setLoadedTemplate(caseReducer.selected_case_template);
      setLoading(false);
      dispatch(caseActions.clearStateMessage("CASE_GET_TEMPLATE"));
    } else if (catchError(caseReducer.status.CASE_GET_TEMPLATE)) {
      showSnackbarError(caseReducer.status.CASE_GET_TEMPLATE);
      setLoading(false);
      dispatch(caseActions.clearStateMessage("CASE_GET_TEMPLATE"));
    }
  }, [
    props.open,
    shouldFetchTemplate,
    caseReducer.status.CASE_GET_TEMPLATE,
    caseReducer.selected_case_template,
    dispatch,
    showSnackbarError,
  ]);

  const setContent = (data) => {
    setMissingPlaceholder(checkPlaceholders(data));
    setTextToContent(data);
    setDidPreviewLetter(false);
  };

  function handleInputChange(e) {
    const { name, value } = e.target;
    setInputs({ ...inputs, [name]: value });
  }

  const sendEmail = (e) => {
    e.preventDefault();

    const updatedInputs = {
      ...inputs,
      content: removeClassesFromHtml(editorInstance.current.getHtmlUnleaned()),
    };
    setInputs(updatedInputs);

    if (
      !missingPlaceholder &&
      updatedInputs.to &&
      updatedInputs.subject &&
      updatedInputs.content
    ) {
      if (!resumeAutomation && saveDeadline()) {
        setTextToContent(
          removeClassesFromHtml(editorInstance.current.getHtmlUnleaned()),
        );
      }
      if (resumeAutomation) {
        resumeAutomation();
      }
      if (activity) {
        setLoading(true);
        dispatch(
          caseActions.addCaseSendEmail(
            caseId,
            updatedInputs,
            attachments,
            activity.id,
          ),
        );
      } else {
        setLoading(true);
        dispatch(
          caseActions.addCaseSendEmail(caseId, updatedInputs, attachments),
        );
      }
    }
  };

  const sendLetter = (e) => {
    e.preventDefault();
    if (!inputs.to) {
      setErrorMessage("Please fill in the website owner contact info.");
      setErrorSnackbar(true);
      return;
    }
    if (
      !opponentContactDetails ||
      !(opponentContactDetails.last_name || opponentContactDetails.company) ||
      !opponentContactDetails.country ||
      !opponentContactDetails.postal_code ||
      !opponentContactDetails.city ||
      !opponentContactDetails.address_line_1
    ) {
      setErrorSnackbarTitle("Sending mail failed");
      setErrorMessage(
        "Please provide address details with name or company, address line 1, city, postal code and country",
      );
      setErrorSnackbar(true);
      return;
    }
    // inputs.content is not kept in sync with editor changes, so read
    // the current content directly from the editor at send time.
    const freshContent = removeClassesFromHtml(
      editorInstance.current.getHtmlUnleaned(),
    );
    const updatedInputs = { ...inputs, content: freshContent };
    if (
      data.template_id &&
      !missingPlaceholder &&
      updatedInputs.to &&
      updatedInputs.subject &&
      updatedInputs.content
    ) {
      setLoading(true);
      setOpenLetterCostPopup(false);
      dispatch(
        caseActions.sendCaseLetter(
          caseId,
          updatedInputs,
          data.template_id,
          data.oldFailedLetterId,
        ),
      );
    }
  };

  const previewLetter = () => {
    if (!inputs.to) {
      setErrorSnackbarTitle("Preview failed");
      setErrorMessage("Please fill in the website owner contact info.");
      setErrorSnackbar(true);
      return;
    }
    if (
      !opponentContactDetails ||
      !(opponentContactDetails.last_name || opponentContactDetails.company) ||
      !opponentContactDetails.country ||
      !opponentContactDetails.postal_code ||
      !opponentContactDetails.city ||
      !opponentContactDetails.address_line_1
    ) {
      setErrorSnackbarTitle("Preview failed");
      setErrorMessage(
        "Please provide address details with name or company, address line 1, city, postal code and country",
      );
      setErrorSnackbar(true);
      return;
    }
    // Same as sendLetter: read from editor since inputs.content may be stale.
    const freshContent = removeClassesFromHtml(
      editorInstance.current.getHtmlUnleaned(),
    );
    const updatedInputs = { ...inputs, content: freshContent };
    if (!updatedInputs.content) {
      showSnackbarError("Please fill in the content.");
      return;
    }
    if (!data.template_id) {
      showSnackbarError("No template selected.");
      return;
    }
    setLoading(true);
    dispatch(
      caseActions.previewCaseLetter(caseId, updatedInputs, data.template_id),
    );
  };

  const handleOpenLetterCostPopup = () => {
    setOpenLetterCostPopup(true);
  };

  const handleCloseLetterCostPopup = () => {
    setOpenLetterCostPopup(false);
    dispatch(caseActions.emptyCaseLetterCost());
  };
  const getLetterCost = () => {
    setLoading(true);
    dispatch(caseActions.getCaseLetterCost(caseId, inputs, data.template_id));
  };

  // All status-driven effects (letter-cost, send email/letter, save-to-log,
  // preview) are owned by this hook so they share the `!open` guard and only
  // the currently-open popup reacts to the global case status.
  useEmailSendStatus({
    open: props.open,
    caseReducer,
    caseId,
    data,
    autoHideDuration,
    isClosingRef,
    closePopup,
    setEditorReady,
    setLoading,
    setErrorSnackbarTitle,
    setErrorMessage,
    setErrorSnackbar,
    setDidPreviewLetter,
    setHasUnreviewedChanges,
    setTextToContent,
    showSnackbarSuccess,
    handleOpenLetterCostPopup,
    handleCloseLetterCostPopup,
  });

  useEffect(() => {
    setDidPreviewLetter(false);
  }, [editedText]);

  const isStandby = caseData?.state === caseConstants.CASE_STATE_STANDBY;
  // The editor is read-only and toolbar is hidden in standby cases.
  useEffect(() => {
    if (!editorReady || !editorInstance.current?.readOnly) return;
    editorInstance.current.readOnly(!!isStandby);
  }, [editorReady, isStandby]);

  const handleDialogClose = () => {
    setAttachments([]);
    resetSnackbar();
    setErrorSnackbar(false);
    dispatch(caseActions.clearStateMessage("CASE_SEND_EMAIL"));
    setEditorReady(false);
    props.close();
  };

  // File attachment: a hidden <input type="file" ref={fileInputRef} /> lives
  // in the JSX. addAttachment() opens the browser file picker by clicking it;
  // handleFileInputChange() runs when the user selects a file.
  const fileInputRef = useRef(null);

  const addAttachment = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileInputChange = useCallback((e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new window.FileReader();
    reader.readAsArrayBuffer(file);
    reader.onloadend = () => {
      setAttachments((oldVal) => [
        ...oldVal,
        {
          filename: file.name,
          content: btoa(
            new Uint8Array(reader.result).reduce(
              (data, byte) => data + String.fromCharCode(byte),
              "",
            ),
          ),
          contentType: file.type,
          encoding: "base64",
        },
      ]);
    };
    e.target.value = "";
  }, []);

  /**
   * Called when checkbox of invoice chooser is changed
   *
   * @param {event} ev Event
   * @param {boolean} value Checkbox checked?
   */
  const handleInvoiceCheckboxChange = (ev, value) => {
    let newInvoices = [...choosenInvoices];

    if (value) {
      newInvoices.push(ev.target.value);
    } else {
      newInvoices.splice(newInvoices.indexOf(ev.target.value), 1);
    }

    setChoosenInvoices(newInvoices);
  };

  /**
   * Reads a blob and converts it
   * to base64 representation
   *
   * @param {Blob} blob Blob to convert
   * @returns {string} base64 of the blob
   */
  const blobToBase64 = (blob) => {
    const reader = new FileReader();
    reader.readAsArrayBuffer(blob);
    return new Promise((resolve) => {
      reader.onloadend = () => {
        resolve(
          btoa(
            new Uint8Array(reader.result).reduce(
              (data, byte) => data + String.fromCharCode(byte),
              "",
            ),
          ),
        );
      };
    });
  };

  // Filter + sort once per activities update; reused in addInvoiceToMail,
  // generateInvoiceList and addChoosenInvoicesToMail.
  const invoiceActivities = React.useMemo(() => {
    const list = caseActivityReducer.activities;
    if (!list || list.length === 0) return [];
    return [...list]
      .filter(
        (x) => x.type === caseConstants.CASEACTIVITY_TYPE_INVOICE_GENERATED,
      )
      .sort((a, b) => new Date(a.created) - new Date(b.created));
  }, [caseActivityReducer.activities]);

  // Preselect when exactly one invoice exists (moved out of render to avoid
  // setState during render).
  useEffect(() => {
    if (
      invoiceActivities.length === 1 &&
      chooseInvoicePopupOpen === false &&
      choosenInvoices.length === 0
    ) {
      setChoosenInvoices([invoiceActivities[0].content.invoice_id]);
    }
  }, [invoiceActivities, chooseInvoicePopupOpen, choosenInvoices.length]);

  /**
   * Depending on the numger of invoices in the case
   * this method opens a popup with info that no invoice is there (count 0)
   * or opens a popup where to choose invoiced (chooseInvoicePopup)
   */
  const addInvoiceToMail = () => {
    if (invoiceActivities.length === 0) {
      setNoInvoicesPopupOpen(true);
    } else {
      setChooseInvoicePopupOpen(true);
    }
  };

  const closeChooseInvoicePopupOpen = () => {
    setChoosenInvoices([]);
    setChooseInvoicePopupOpen(false);
  };

  const addChoosenInvoicesToMail = () => {
    const findActivityByInvoiceId = (invoiceId) =>
      invoiceActivities.find((a) => a.content?.invoice_id === invoiceId);

    for (const invoiceId of choosenInvoices) {
      const activity = findActivityByInvoiceId(invoiceId);
      if (activity) downloadAndAddInvoice(activity);
    }

    closeChooseInvoicePopupOpen();
  };

  return (
    <>
      <AppSnackbar {...snackbarProps} />
      <DialogPopup
        title={
          <span className={styles.title}>
            <EmailRoundedIcon className={styles.title_icon} />
            {title ||
              ` ${
                data.name === emailConstants.NEW_MAIL
                  ? "New mail"
                  : "Mail preview: " + data.name
              }`}
          </span>
        }
        open={props.open}
        onClose={(e, reason) => {
          if (reason === "backdropClick") return;
          setAttachments([]);
          props.close();
        }}
        size={"large"}
        disableEscapeKeyDown={true}
      >
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileInputChange}
          style={{ display: "none" }}
        />
        {errorSnackbar && (
          <ErrorMessagePopup
            popupTitle={errorSnackbarTitle || "Sending mail failed"}
            open={errorSnackbar}
            close={() => setErrorSnackbar(false)}
            errorMessage={errorMessage}
          />
        )}
        <div className={styles.send_email_title}>
          <AddressComponent
            emailTo={inputs.to}
            emailCc={inputs.cc}
            handleEmailToChanged={(s) =>
              handleInputChange({ target: { name: "to", value: s } })
            }
            handleSubjectChanged={(s) =>
              handleInputChange({ target: { name: "subject", value: s } })
            }
            handleEmailBccChanged={(s) =>
              handleInputChange({ target: { name: "bcc", value: s } })
            }
            handleEmailCcChanged={(s) =>
              handleInputChange({ target: { name: "cc", value: s } })
            }
            isReplyMode={isReplyMode}
            sendEmailTag={sendEmailTag}
            subject={inputs.subject}
          />
        </div>
        <div
          className={
            missingPlaceholder
              ? styles.placeholder_missing
              : styles.placeholder_missing_none
          }
        >
          <ErrorIcon className={styles.placeholder_message_icon} />
          <p className={styles.placeholder_message}>
            The marked elements do not have a value. Please add the missing
            information.
          </p>
        </div>
        <div
          style={{ maxHeight: "58vh", overflowY: "auto", overflowX: "hidden" }}
          ref={contentRef}
        >
          <Scrollbar maxHeight="57vh" options={{ suppressScrollX: true }}>
            <RichTextEditor
              addAttachmentCallback={addAttachment}
              setAttachmentsCallback={(newAttachment) =>
                setAttachments((oldVal) => [...oldVal, newAttachment])
              }
              addInvoiceCallback={addInvoiceToMail}
              setInvoiceCallback={(newInvoiceAttachement) =>
                setAttachments((oldVal) => [...oldVal, newInvoiceAttachement])
              }
              setMissingPlaceholderCallback={setMissingPlaceholder}
              setContentCallback={setContent}
              reference={editorInstance}
              onEditorReady={() => setEditorReady(true)}
              onContentChange={() => {
                setHasUnreviewedChanges(true);
              }}
              hideToolbar={caseData?.state === caseConstants.CASE_STATE_STANDBY}
              readOnly={caseData?.state === caseConstants.CASE_STATE_STANDBY}
            />
          </Scrollbar>
        </div>
        <AttachmentsRow
          attachments={attachments}
          onRemove={(x) => setAttachments(attachments.filter((y) => y !== x))}
        />
        <div className="action_container">
          {caseData?.state !== caseConstants.CASE_STATE_STANDBY &&
            !resumeAutomation && (
              <ReminderControls
                doSaveDeadline={doSaveDeadline}
                setReminderChecked={setReminderChecked}
                reminderMenuOpen={reminderMenuOpen}
                handleOpenReminderMenu={handleOpenReminderMenu}
                handleCloseReminderMenu={handleCloseReminderMenu}
                reminderSetting={reminderSetting}
                handleChangeReminderOption={handleChangeReminderOption}
                showDatePicker={showDatePicker}
                reminderDate={reminderDate}
                setReminderDate={setReminderDate}
                reminderMinDate={reminderMinDate}
                reminderName={reminderName}
                setReminderName={setReminderName}
              />
            )}
          <PopupFooter
            loading={loading}
            dataType={data?.type}
            caseState={caseData?.state}
            sendEmailTag={sendEmailTag}
            missingPlaceholder={missingPlaceholder}
            inputsTo={inputs.to}
            inputsSubject={inputs.subject}
            didPreviewLetter={didPreviewLetter}
            hasUnreviewedChanges={hasUnreviewedChanges}
            onClose={handleDialogClose}
            onPreview={previewLetter}
            onSend={
              data?.type === emailConstants.TEMPLATE_TYPE_LETTER
                ? getLetterCost
                : sendEmail
            }
          />
        </div>
        <AreYouSurePopup
          open={showAreYouSurePopup}
          close={handleAreYouSurePopupClose}
          onClick={handleDeleteReminder}
        >
          You currently have a reminder set: Would you like to delete it?
        </AreYouSurePopup>
        <InvoicePicker
          invoiceActivities={invoiceActivities}
          noInvoicesPopupOpen={noInvoicesPopupOpen}
          onCloseNoInvoices={() => setNoInvoicesPopupOpen(false)}
          caseIdLabel={caseData?.case_id}
          chooseInvoicePopupOpen={chooseInvoicePopupOpen}
          onCloseChooseInvoice={() => closeChooseInvoicePopupOpen()}
          onConfirmChooseInvoice={() => addChoosenInvoicesToMail()}
          handleInvoiceCheckboxChange={handleInvoiceCheckboxChange}
        />
        <LetterCostDialog
          open={openLetterCostPopup}
          onClose={() => handleCloseLetterCostPopup()}
          onConfirm={sendLetter}
          letterCost={caseReducer.letter_cost}
          credits={customerReducer.credits}
        />
      </DialogPopup>
    </>
  );

  function downloadAndAddInvoice(activity) {
    const invoiceDownloadErrorMessage =
      "Failed to download Invoice for attachment. ";
    invoiceTemplateService
      .downloadInvoice(
        activity.case_id,
        activity.content.invoice_id,
        activity.content.invoice_number,
      )
      .then((data) => {
        data
          .blob()
          .then((blob) => {
            let b = new Blob([blob], { type: "application/pdf" });
            blobToBase64(b)
              .then((b64) => {
                setAttachments((oldVal) => {
                  return [
                    ...oldVal,
                    ...[
                      {
                        filename: activity.content.invoice_number + ".pdf",
                        content: b64,
                        contentType: "application/pdf",
                        encoding: "base64",
                      },
                    ],
                  ];
                });
              })
              .catch((e) => {
                setErrorMessage(invoiceDownloadErrorMessage);
                setErrorSnackbar(true);
              });
          })
          .catch((e) => {
            setErrorMessage(invoiceDownloadErrorMessage);
            setErrorSnackbar(true);
          });
      })
      .catch((err) => {
        setErrorMessage(invoiceDownloadErrorMessage);
        setErrorSnackbar(true);
      });
  }
}
export default MailPopup;
